import React, { useEffect, useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import type { Me } from '../App';
import { api, ApiError } from '../api';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';

interface Profile {
  id: string;
  email: string;
  name?: string | null;
  role: 'ADMIN' | 'USER';
  organizerEmail: string;
  createdAt: string;
}

interface Passkey {
  id: string;
  credentialId: string;
  transports?: string | null;
  deviceLabel?: string | null;
  createdAt: string;
  lastUsedAt?: string | null;
}

export function SettingsPage({ me, onUpdate }: { me: Me; onUpdate: (me: Me) => void }) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [passkeys, setPasskeys] = useState<Passkey[]>([]);
  const [profileForm, setProfileForm] = useState({ name: '', organizerEmail: '' });
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '' });
  const toast = useToast();

  const load = async () => {
    const [p, keys] = await Promise.all([
      api.get<Profile>('/api/profile'),
      api.get<Passkey[]>('/api/profile/passkeys'),
    ]);
    setProfile(p);
    setProfileForm({ name: p.name ?? '', organizerEmail: p.organizerEmail });
    setPasskeys(keys);
  };

  useEffect(() => {
    load();
  }, []);

  const saveProfile = async () => {
    try {
      const updated = await api.patch<Me>('/api/profile', {
        name: profileForm.name || null,
        organizerEmail: profileForm.organizerEmail,
      });
      onUpdate({ ...me, ...updated, organizerEmail: updated.organizerEmail ?? updated.email });
      toast.success('Profil gespeichert');
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Profil konnte nicht gespeichert werden');
    }
  };

  const changePassword = async () => {
    try {
      await api.post('/api/profile/password', passwordForm);
      setPasswordForm({ currentPassword: '', newPassword: '' });
      toast.success('Passwort geaendert');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Passwort konnte nicht geaendert werden');
    }
  };

  const addPasskey = async () => {
    try {
      const opts = await api.post<never>('/api/auth/passkey/register/options');
      const response = await startRegistration(opts);
      await api.post('/api/auth/passkey/register/verify', {
        response,
        deviceLabel: navigator.userAgent,
      });
      toast.success('Passkey hinzugefuegt');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Passkey konnte nicht hinzugefuegt werden');
    }
  };

  const removePasskey = async (id: string) => {
    await api.delete(`/api/profile/passkeys/${id}`);
    toast.success('Passkey entfernt');
    load();
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-h3">Einstellungen</h1>
        {profile && <Badge tone={profile.role === 'ADMIN' ? 'info' : 'muted'}>{profile.role}</Badge>}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr,420px] gap-6">
        <section className="card">
          <h2 className="text-h4 mb-4">Profil</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">E-Mail</label>
              <input className="input font-mono" value={profile?.email ?? ''} disabled />
            </div>
            <div>
              <label className="label">Angelegt</label>
              <input className="input" value={profile ? new Date(profile.createdAt).toLocaleDateString('de-DE') : ''} disabled />
            </div>
            <div>
              <label className="label">Name</label>
              <input className="input" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} />
            </div>
            <div>
              <label className="label">Organisator-Adresse</label>
              <input className="input font-mono" value={profileForm.organizerEmail} onChange={(e) => setProfileForm({ ...profileForm, organizerEmail: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button className="btn btn-primary" onClick={saveProfile}>Speichern</button>
          </div>
        </section>

        <section className="card">
          <h2 className="text-h4 mb-4">Passwort</h2>
          <div className="flex flex-col gap-3">
            <div>
              <label className="label">Aktuelles Passwort</label>
              <input type="password" className="input" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })} />
            </div>
            <div>
              <label className="label">Neues Passwort</label>
              <input type="password" className="input" value={passwordForm.newPassword} onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })} />
            </div>
            <button className="btn btn-secondary" onClick={changePassword}>Passwort aendern</button>
          </div>
        </section>
      </div>

      <section className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-h4">Passkeys</h2>
          <button className="btn btn-primary" onClick={addPasskey}>+ Passkey</button>
        </div>
        <div className="grid gap-3">
          {passkeys.map((key) => (
            <div key={key.id} className="bg-surface-elevated border border-border rounded-md p-4 flex items-center gap-4">
              <div className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center font-mono text-small">PK</div>
              <div className="flex-1 min-w-0">
                <div className="text-body truncate">{key.deviceLabel ?? 'Passkey'}</div>
                <div className="text-small text-ink-muted">
                  Angelegt {new Date(key.createdAt).toLocaleDateString('de-DE')}
                  {key.lastUsedAt ? ` · zuletzt ${new Date(key.lastUsedAt).toLocaleDateString('de-DE')}` : ''}
                </div>
              </div>
              <button className="btn btn-danger" onClick={() => removePasskey(key.id)}>Entfernen</button>
            </div>
          ))}
          {passkeys.length === 0 && <div className="text-small text-ink-muted py-4">Noch keine Passkeys registriert.</div>}
        </div>
      </section>
    </div>
  );
}
